export type ScheduleViewMode = 'smart' | 'calendar' | 'list';
export type LegacyCalendarType = 'daily' | 'weekly';
export type SmartSchedulePeriodPreference = 'month' | 'week' | 'day';

/**
 * 스케줄 보기 설정의 저장 범위.
 * 센터 + 관리자 계정 단위로 저장한다. 한 PC를 여러 직원이 공유해도 서로의 선택을
 * 덮어쓰지 않게 하기 위함이다.
 */
export type ScheduleViewScope = {
    centerId?: string;
    accountId?: string;
};

const SCHEDULE_VIEW_MODE_STORAGE_KEY = 'rappo:schedule-view-mode:v1';
const LEGACY_CALENDAR_TYPE_STORAGE_KEY = 'rappo:legacy-calendar-type:v1';
const SMART_SCHEDULE_PERIOD_STORAGE_KEY = 'rappo:smart-schedule-period:v1';

const scopedStorageKey = (baseKey: string, scope?: ScheduleViewScope) =>
    `${baseKey}:${scope?.centerId || 'default'}:${scope?.accountId || 'shared'}`;

/** 계정별 분리 이전에 쓰던 센터 단위 키 */
const centerStorageKey = (baseKey: string, scope?: ScheduleViewScope) => `${baseKey}:${scope?.centerId || 'default'}`;

const readPreference = (baseKey: string, scope?: ScheduleViewScope): string | null => {
    try {
        const scopedValue = window.localStorage.getItem(scopedStorageKey(baseKey, scope));
        if (scopedValue !== null) return scopedValue;
        // 계정별 키 도입 전에 저장해 둔 값이 있으면 그대로 이어서 쓴다.
        return window.localStorage.getItem(centerStorageKey(baseKey, scope));
    } catch {
        return null;
    }
};

const writePreference = (baseKey: string, scope: ScheduleViewScope | undefined, value: string) => {
    try {
        window.localStorage.setItem(scopedStorageKey(baseKey, scope), value);
    } catch {
        // Preference persistence should not block schedule operations.
    }
};

export const readScheduleViewMode = (scope?: ScheduleViewScope): ScheduleViewMode => {
    const savedViewMode = readPreference(SCHEDULE_VIEW_MODE_STORAGE_KEY, scope);
    return savedViewMode === 'calendar' || savedViewMode === 'list' || savedViewMode === 'smart'
        ? savedViewMode
        : 'smart';
};

export const writeScheduleViewMode = (scope: ScheduleViewScope | undefined, viewMode: ScheduleViewMode) => {
    writePreference(SCHEDULE_VIEW_MODE_STORAGE_KEY, scope, viewMode);
};

export const readLegacyCalendarType = (scope?: ScheduleViewScope): LegacyCalendarType =>
    readPreference(LEGACY_CALENDAR_TYPE_STORAGE_KEY, scope) === 'weekly' ? 'weekly' : 'daily';

export const writeLegacyCalendarType = (scope: ScheduleViewScope | undefined, calendarType: LegacyCalendarType) => {
    writePreference(LEGACY_CALENDAR_TYPE_STORAGE_KEY, scope, calendarType);
};

export const readSmartSchedulePeriod = (scope?: ScheduleViewScope): SmartSchedulePeriodPreference => {
    const savedPeriod = readPreference(SMART_SCHEDULE_PERIOD_STORAGE_KEY, scope);
    return savedPeriod === 'month' || savedPeriod === 'day' || savedPeriod === 'week' ? savedPeriod : 'day';
};

export const writeSmartSchedulePeriod = (
    scope: ScheduleViewScope | undefined,
    period: SmartSchedulePeriodPreference
) => {
    writePreference(SMART_SCHEDULE_PERIOD_STORAGE_KEY, scope, period);
};

/** 훅 의존성 배열/하이드레이션 비교용 안정 키 */
export const scheduleViewScopeKey = (scope?: ScheduleViewScope) =>
    `${scope?.centerId || 'default'}:${scope?.accountId || 'shared'}`;
